import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import axios from 'axios';

/**
 * Phase 17: Step 3 - Seniors Overview
 * Show existing seniors or prompt to add
 */
function Step3Seniors() {
  const navigate = useNavigate();
  const { household, isAdmin } = useFamilyAuth();
  const [seniors, setSeniors] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (household) {
      loadSeniors();
    }
  }, [household]);

  const loadSeniors = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/households/${household.id}/seniors`);
      setSeniors(response.data);
    } catch (error) {
      console.error('Error loading seniors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSenior = () => {
    window.open('/dashboard', '_blank');
  };

  const handleNext = () => {
    navigate('/onboarding/step4');
  };

  if (loading) {
    return (
      <div className="w-full max-w-2xl mx-auto text-center">
        <div className="animate-spin w-16 h-16 border-4 border-white/30 border-t-white rounded-full mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-2xl mx-auto animate-fade-in">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white/50"></div>
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
        </div>
      </div>

      {/* Card */}
      <div className="bg-neutral-900/90 backdrop-blur-xl rounded-3xl border border-neutral-600/40 p-8 sm:p-12 shadow-2xl animate-scale-in">
        {/* Icon */}
        <div className="text-center mb-6">
          <span className="text-7xl">👴👵</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#F9FAFB] mb-2 text-center">
          Wer lebt hier?
        </h1>
        <p className="text-neutral-500 mb-8 text-center">
          {seniors.length > 0 ? 'Ihre Senioren' : 'Fügen Sie Senioren hinzu'}
        </p>

        {/* Seniors List or Empty State */}
        <div className="mb-8">
          {seniors.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-hayat-neutral-300 rounded-2xl">
              <p className="text-hayat-neutral-500 mb-6">
                Sie haben noch keine Senioren hinzugefügt.
              </p>
              {isAdmin && (
                <button
                  onClick={handleAddSenior}
                  className="px-8 py-3 bg-hayat-secondary-500 text-white rounded-2xl font-semibold hover:bg-hayat-secondary-600 transition-colors"
                >
                  Senior hinzufügen
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {seniors.map((senior, idx) => (
                <div
                  key={senior.id}
                  className="flex items-center gap-4 p-4 bg-hayat-neutral-50 rounded-2xl hover:bg-hayat-neutral-100 transition-colors animate-scale-in"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-hayat-secondary-500 to-hayat-secondary-600 flex items-center justify-center text-white text-2xl font-bold shadow-hayat-sm">
                    {senior.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-[#F9FAFB] text-lg">{senior.name}</h3>
                    <p className="text-sm text-neutral-500">
                      {senior.language === 'de' && '🇩🇪 Deutsch'}
                      {senior.language === 'tr' && '🇹🇷 Türkisch'}
                      {senior.language === 'ar' && '🇸🇦 Arabisch'}
                      {senior.language === 'en' && '🇬🇧 Englisch'}
                    </p>
                  </div>
                  <span className="text-hayat-success-600 text-2xl">✓</span>
                </div>
              ))}
              
              {isAdmin && (
                <button
                  onClick={handleAddSenior}
                  className="w-full py-4 border-2 border-dashed border-hayat-neutral-300 rounded-2xl text-neutral-500 font-semibold hover:border-hayat-secondary-400 hover:text-hayat-secondary-600 transition-colors"
                >
                  + Weiteren Senior hinzufügen
                </button>
              )}
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-hayat-secondary-50 border border-hayat-secondary-200 rounded-2xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <span className="text-xl">ℹ️</span>
            <p className="text-sm text-hayat-secondary-900">
              Senioren können über das <strong>Caregiver-Dashboard</strong> hinzugefügt und verwaltet werden.
            </p>
          </div>
        </div>

        {/* Button */}
        <button
          onClick={handleNext}
          className="w-full py-5 bg-gradient-to-r from-hayat-primary-500 to-hayat-primary-600 text-white rounded-2xl font-bold text-lg shadow-hayat-lg hover:shadow-hayat-xl hover:scale-105 active:scale-95 transition-all duration-300"
        >
          Weiter →
        </button>
      </div>
    </div>
  );
}

export default Step3Seniors;